import { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';

const API = 'https://affinityconcept.tech/api';

const CATEGORIES = {
  'Groceries': ['Supermarket', 'Online Delivery'],
  'Dining': ['Restaurant', 'Cafe', 'Delivery', 'Fast Food', 'Catering'],
  'Transport': ['Fuel', 'Taxi/Uber', 'Parking', 'Car Service', 'Toll', 'Car Rental'],
  'Utilities': ['DEWA', 'Internet', 'Phone', 'Gas'],
  'Education': ['School Fees', 'Books', 'Uniforms', 'Piano', 'Tennis', 'Gymnastics', 'Kids Activities'],
  'Healthcare': ['Doctor', 'Hospital', 'Pharmacy', 'Dentist', 'Gym', 'Spa', 'Insurance'],
  'Beauty': ['Hairdresser', 'Nails', 'Cosmetics', 'Skincare', 'Spa & Massage', 'Other'],
  'Shopping': ['Clothes & Bags', 'Shoes', 'Makeup & Hair', 'Electronics', 'Jewelry & Watches', 'Kids Shopping', 'General Shopping'],
  'Housing': ['Rent', 'Furniture', 'Appliances', 'Maintenance', 'Cleaning'],
  'Entertainment': ['Cinema', 'Events', 'Streaming', 'Sports', 'Hobbies'],
  'Travel': ['Flights', 'Hotels', 'Car Rental', 'Activities', 'Visa Fees', 'Vacations'],
  'Gifts': ['Friends & Family', 'Occasions', 'Other'],
  'Pet (Charlie)': ['Food & Supplies', 'Veterinarian', 'Grooming', 'Other'],
  'Home Staff': ['Nanny', 'Babysitter', 'Cleaning', 'Extra Hours', 'Other'],
  'Investment': ['Stocks', 'Crypto', 'Real Estate', 'Funds', 'Other'],
  'Other': ['Miscellaneous'],
};

const CATEGORY_ICONS = {
  'Groceries': '🛒', 'Dining': '🍽️', 'Transport': '🚗', 'Utilities': '💡',
  'Education': '🎓', 'Healthcare': '❤️', 'Beauty': '💅', 'Shopping': '🛍️', 'Housing': '🏠',
  'Entertainment': '🎬', 'Travel': '✈️', 'Gifts': '🎁', 'Pet (Charlie)': '🐾',
  'Home Staff': '👩‍👧',  'Investment': '📈', 'Other': '📦'
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const FORM_DEFAULT = {
  merchant: '',
  amount: '',
  currency: 'AED',
  category: 'Groceries',
  subcategory: 'Supermarket',
  date: new Date().toISOString().split('T')[0],
  description: '',
};

export default function Expenses({ token, onLogout }) {
  const now = new Date();
  const [expenses, setExpenses] = useState([]);
  const [user, setUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(FORM_DEFAULT);
  const [filterCategory, setFilterCategory] = useState('All');
  const [selectedMonth, setSelectedMonth] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/expenses`, { headers }).then(r => setExpenses(r.data));
    axios.get(`${API}/me`, { headers }).then(r => setUser(r.data));
  }, []);

  const navigateMonth = (dir) => {
    setSelectedMonth(prev => {
      let m = prev.month + dir;
      let y = prev.year;
      if (m > 12) { m = 1; y++; }
      if (m < 1) { m = 12; y--; }
      return { year: y, month: m };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: parseFloat(form.amount) };
    if (editItem) {
      await axios.put(`${API}/expenses/${editItem.id}`, payload, { headers });
    } else {
      await axios.post(`${API}/expenses`, payload, { headers });
    }
    const res = await axios.get(`${API}/expenses`, { headers });
    setExpenses(res.data);
    setShowForm(false);
    setEditItem(null);
    setForm(FORM_DEFAULT);
  };

  const handleEdit = (expense) => {
    setEditItem(expense);
    setForm({
      merchant: expense.merchant,
      amount: expense.amount,
      currency: expense.currency,
      category: expense.category,
      subcategory: expense.subcategory || '',
      date: expense.date ? expense.date.split('T')[0] : new Date().toISOString().split('T')[0],
      description: expense.description || '',
    });
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    await axios.delete(`${API}/expenses/${id}`, { headers });
    setExpenses(expenses.filter(e => e.id !== id));
  };

  const handleToggleForecast = async (id) => {
    await axios.patch(`${API}/expenses/${id}/toggle-forecast`, {}, { headers });
    const res = await axios.get(`${API}/expenses`, { headers });
    setExpenses(res.data);
  };

  // Filter by selected month
  const monthFiltered = expenses.filter(e => {
    const d = new Date(e.date);
    return d.getMonth() + 1 === selectedMonth.month && d.getFullYear() === selectedMonth.year;
  });

  // Filter by category
  const filtered = filterCategory === 'All'
    ? monthFiltered
    : monthFiltered.filter(e => e.category === filterCategory);

  const total = monthFiltered.reduce((sum, e) => sum + e.amount, 0);
  const filteredTotal = filtered.reduce((sum, e) => sum + e.amount, 0);

  const selectedMonthName = new Date(selectedMonth.year, selectedMonth.month - 1, 1)
    .toLocaleDateString('en-AE', { month: 'long', year: 'numeric' });

  const isCurrentMonth = selectedMonth.year === now.getFullYear() && selectedMonth.month === now.getMonth() + 1;

  // Category breakdown for selected month
  const categoryBreakdown = monthFiltered.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + e.amount;
    return acc;
  }, {});

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar onLogout={onLogout} user={user} />
      <main className="flex-1 overflow-y-auto p-4 md:p-8 pt-16 md:pt-8">

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Expenses</h1>
            <p className="text-gray-500 text-sm">
              {selectedMonthName}: <span className="font-semibold text-red-500">AED {total.toLocaleString()}</span>
              {filterCategory !== 'All' && <span> · {filterCategory}: <span className="font-semibold">AED {filteredTotal.toLocaleString()}</span></span>}
            </p>
          </div>
          <button
            onClick={() => { setShowForm(!showForm); setEditItem(null); setForm(FORM_DEFAULT); }}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition">
            + Add
          </button>
        </div>

        {/* Month Navigator */}
        <div className="flex items-center justify-between bg-white rounded-2xl px-5 py-3 shadow-sm border border-gray-100 mb-4">
          <button onClick={() => navigateMonth(-1)}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-xl transition text-gray-500 font-bold text-xl">
            ‹
          </button>
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-900">{selectedMonthName}</p>
            <p className="text-xs text-gray-400">
              {isCurrentMonth ? '📍 Current month' : '📊 Historical'}
              {' · '}{monthFiltered.length} transactions
            </p>
          </div>
          <button onClick={() => navigateMonth(1)}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-xl transition text-gray-500 font-bold text-xl">
            ›
          </button>
        </div>

        {/* Summary cards */}
        {monthFiltered.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {Object.entries(categoryBreakdown)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 4)
              .map(([cat, amt]) => (
                <div key={cat} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 cursor-pointer hover:border-green-300 transition"
                  onClick={() => setFilterCategory(filterCategory === cat ? 'All' : cat)}>
                  <p className="text-xs text-gray-500">{CATEGORY_ICONS[cat]} {cat}</p>
                  <p className="text-lg font-bold text-gray-900 mt-1">AED {amt.toLocaleString()}</p>
                  <p className="text-xs text-gray-400">{((amt/total)*100).toFixed(0)}% of total</p>
                </div>
              ))}
          </div>
        )}

        {/* Add/Edit form */}
        {showForm && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-4">
            <h2 className="text-base font-semibold mb-4">{editItem ? '✏️ Edit Expense' : '+ New Expense'}</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input required placeholder="Merchant / Description"
                value={form.merchant} onChange={e => setForm({...form, merchant: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              <div className="flex gap-2">
                <input required type="number" step="any" placeholder="Amount"
                  value={form.amount} onChange={e => setForm({...form, amount: e.target.value})}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-green-500" />
                <select value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none">
                  <option>AED</option>
                  <option>USD</option>
                </select>
              </div>
              <select value={form.category}
                onChange={e => setForm({...form, category: e.target.value, subcategory: CATEGORIES[e.target.value][0]})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                {Object.keys(CATEGORIES).map(c => <option key={c}>{c}</option>)}
              </select>
              <select value={form.subcategory} onChange={e => setForm({...form, subcategory: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                {(CATEGORIES[form.category] || []).map(s => <option key={s}>{s}</option>)}
              </select>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Date</label>
                <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <input placeholder="Notes (optional)"
                value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              <div className="md:col-span-2 flex gap-3">
                <button type="submit" className="bg-green-600 text-white px-5 py-2 rounded-xl text-sm font-medium">
                  {editItem ? 'Update' : 'Save'}
                </button>
                <button type="button" onClick={handleCancel}
                  className="border border-gray-200 px-5 py-2 rounded-xl text-sm">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Category filters */}
        <div className="flex gap-2 flex-wrap mb-4">
          {['All', ...Object.keys(CATEGORIES)].map(cat => (
            <button key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                filterCategory === cat
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-green-300'
              }`}>
              {CATEGORY_ICONS[cat] || ''} {cat}
            </button>
          ))}
        </div>

        {/* Expenses table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Merchant</th>
                <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Category</th>
                <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Subcategory</th>
                <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Date</th>
                <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="6" className="p-8 text-center text-gray-400 text-sm">
                  No expenses for {selectedMonthName}{filterCategory !== 'All' ? ` in ${filterCategory}` : ''}
                </td></tr>
              )}
              {filtered.map(e => (
                <tr key={e.id} className={`border-b border-gray-50 hover:bg-gray-50 ${e.exclude_from_forecast ? 'opacity-60' : ''}`}>
                  <td className="p-4">
                    <p className="text-sm font-medium text-gray-900">{e.merchant}</p>
                    {e.description && <p className="text-xs text-gray-400 mt-0.5">{e.description}</p>}
                    {e.exclude_from_forecast && <p className="text-xs text-gray-300 mt-0.5">excluded from forecast</p>}
                    <p className="text-xs text-gray-400 md:hidden mt-0.5">
                      {e.date ? new Date(e.date).toLocaleDateString('en-GB') : ''}
                    </p>
                  </td>
                  <td className="p-4 hidden md:table-cell">
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                      {CATEGORY_ICONS[e.category]} {e.category}
                    </span>
                  </td>
                  <td className="p-4 text-xs text-gray-500 hidden md:table-cell">{e.subcategory}</td>
                  <td className="p-4 text-sm text-gray-500 hidden md:table-cell">
                    {e.date ? new Date(e.date).toLocaleDateString('en-GB') : ''}
                  </td>
                  <td className="p-4 text-right">
                    <p className="text-sm font-semibold text-red-500">{e.currency} {e.amount?.toLocaleString()}</p>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleToggleForecast(e.id)}
                        title={e.exclude_from_forecast ? 'Excluded from forecast - click to include' : 'Included in forecast - click to exclude'}
                        className="transition">
                        {e.exclude_from_forecast ? '⚪' : '🔵'}
                      </button>
                      <button onClick={() => handleEdit(e)}
                        className="text-gray-400 hover:text-blue-500 transition">✏️</button>
                      <button onClick={() => handleDelete(e.id)}
                        className="text-gray-300 hover:text-red-500 transition">✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </main>
    </div>
  );

  function handleCancel() {
    setShowForm(false);
    setEditItem(null);
    setForm(FORM_DEFAULT);
  }
}
